import { useState, useEffect, useCallback, useRef } from 'react';
import { useGetDownloadLogsQuery } from './downloadLog.api.slice';
import { IDownloadLog } from './downloadLog';

interface UseDownloadLogsReturn {
  logs: IDownloadLog[];
  isLoading: boolean;
  error: any;
  hasMore: boolean;
  lastLogElementRef: (node: HTMLTableRowElement) => void;
}

const useDownloadLogs = (limit: number = 10): UseDownloadLogsReturn => {
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState<IDownloadLog[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver>();

  const { data, isLoading, error } = useGetDownloadLogsQuery({
    page,
    limit,
  });

  // Update logs when new data arrives
  useEffect(() => {
    if (data?.data) {
      if (page === 1) {
        setLogs(data.data.downloadLogs);
      } else {
        setLogs((prev) => [...prev, ...data.data.downloadLogs]);
      }

      setHasMore(page < data.data.pagination.totalPages);
    }
  }, [data, page]);

  // Intersection observer for infinite scroll
  const lastLogElementRef = useCallback(
    (node: HTMLTableRowElement) => {
      if (isLoading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  return {
    logs,
    isLoading,
    error,
    hasMore,
    lastLogElementRef,
  };
};

export default useDownloadLogs;
